import loadContextData from "../../utils/loadContextData";
import loadPreactModule from "../../utils/loadPreactModule";
import renderPreactWithProps from "../../utils/renderPreactWithProps";
import callIfArgumentsPass from "../../utils/callIfArgumentsPass";
import curry from "../../lib/core/functional/curry";
import noop from "../../lib/core/functional/noop";
import transport from "../../transport/follow";

const doesFollow = curry((entity, entityId, userInfo) => {
    return userInfo.profileSite.follow[entity].includes(entityId);
});

const didUserInfoChanged = curry((follows, prevErr, prevUserInfo, err, userInfo) => {
    if (prevErr != err) {
        return true;
    }

    return follows(userInfo) !== follows(prevUserInfo);
});

export const renderFollowButton = curry(
    (isUserFollowing, context, entity, entityId, renderModule, callTransport, err, userInfo) => {
        let userId = null;
        let isFollowing = false;
        let isLoggedIn = false;
        let inProgress = false;

        if (!err) {
            isLoggedIn = true;
            isFollowing = isUserFollowing(userInfo);
            userId = userInfo.profileSite.id;
        }

        if (userId && entity === "sites" && userId === entityId) {
            return;
        }

        const toggleFollow = curry((following, event) => {
            if (!isLoggedIn || inProgress) {
                return;
            }
            inProgress = true;

            callTransport(userId, !following).then(onTransportCallSuccess).catch(console.error);
            event.preventDefault();
            return false;
        });

        function onTransportCallSuccess(res) {
            context.refreshUserInfo();
            makePropsAndRender(res.status);
        }

        function makePropsAndRender(following) {
            const props = {
                isLoggedIn,
                following,
                toggleFollow: isLoggedIn ? toggleFollow(following) : noop,
            };

            renderModule(props);
        }

        makePropsAndRender(isFollowing);
    }
);

const onModule = curry((context, element, [renderer, Module]) => {
    let entity, entityId;
    const runnerId = context.settings.runner.id;

    if ("authorId" in element.dataset) {
        entity = "sites";
        entityId = parseInt(element.dataset.authorId, 10);
    } else {
        const parentPost = element.closest("article[elid]");

        if (parentPost) {
            const contextData = loadContextData(
                `.post-context-${parentPost.getAttribute("elid")}`,
                parentPost
            );
            entity = "sites";
            entityId = contextData.post.authors[0];
        } else {
            if (context.settings.section.id) {
                entity = "sections";
                entityId = context.settings.section.id;
            } else if (context.settings.tag.slug) {
                entity = "tags";
                entityId = context.settings.tag.slug;
            } else {
                entity = "sites";
                entityId = context.settings.site.id;
            }
        }
    }

    const isFollowingEntity = doesFollow(entity, entityId);

    context.listenForUserInfo(
        callIfArgumentsPass(
            didUserInfoChanged(isFollowingEntity),
            renderFollowButton(
                isFollowingEntity,
                context,
                entity,
                entityId,
                renderPreactWithProps(renderer, Module, element),
                transport(entity, entityId, runnerId)
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);

export default function renderer(context, element) {
    loadPreactModule("FollowButton", context, {}, internalLoader).then(onModule(context, element));
}
